import React from 'react';
import { useViewport } from '@xyflow/react';
import { renderFrameOnlySVG, SHEET, planCableListPages, planVlanListPages } from '@/lib/svgExport';
import { useStore, getActivePage } from '@/state/store';
import { pageDisplayName } from '@/lib/pageNames';

// Renders the CAD sheet (frame + title block + legend) as a viewport-synced
// background BEHIND the React Flow edge/node layers. Rendering it as a node would
// cover the edges (the nodes layer always paints above the edges SVG in React Flow).
//
// Per-page element zoom: the device/edge layer (React Flow content) stays at its
// stored logical coordinates; instead we scale the FRAME by 1/s so the elements
// appear scaled by `s` RELATIVE to the fixed frame. This keeps React Flow handles
// and edges pixel-accurate (no node re-measuring) while frame/legend/title block
// keep their absolute proportions — matching the `<g scale(s)>` used in exports.
export const SheetBackground = ({ overlay = false }) => {
  const { x, y, zoom } = useViewport();
  const doc = useStore((s) => s.doc);
  const lang = useStore((s) => s.language);
  const showLegend = useStore((s) => s.showVlanLegend);
  const page = useStore(getActivePage);
  const idx = doc.pages.findIndex((p) => p.id === page?.id);
  const total = doc.pages.length + planCableListPages(doc, lang).length + planVlanListPages(doc, lang).length;
  // On location pages the element zoom scales ONLY the markers, so the frame is
  // kept at the fixed sheet scale (s = 1) and stays aligned with the background.
  const s = page?.type === 'location' ? 1 : page?.contentScale || 1;
  const svg = renderFrameOnlySVG(
    doc,
    lang,
    { index: idx >= 0 ? idx + 1 : 1, total },
    pageDisplayName(page?.name, lang),
    { page, showLegend, transparent: overlay }
  );

  return (
    <div className={`pointer-events-none absolute inset-0 overflow-hidden ${overlay ? 'z-[5]' : ''}`} aria-hidden="true">
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: SHEET.w,
          height: SHEET.h,
          transform: `translate(${x}px, ${y}px) scale(${zoom / s})`,
          transformOrigin: '0 0',
        }}
        dangerouslySetInnerHTML={{ __html: svg }}
      />
    </div>
  );
};
